import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import Confetti from '../animation/Confetti';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { useSetErrorAfterTimeout } from '../hooks/useSetErrorAfterTimeout';
import { getActiveLoading, getCanSwitchCopy } from '../store/loading-status/selectors';
import { LoadingCentered } from './Loading.styles';
import { LoadingTitle } from './LoadingTitle';
import { useCopyCarousel } from './useCopyCarousel';
import { useLoadingTitle } from './useLoadingTitle';
import styled from 'styled-components';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { LayoutType } from 'signup-components/types';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Image } from 'foundations-components/transitional/components/Image';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { Data } from 'signup-constants/signupData/Data';
import { useSignupData } from '../signup-data/useSignupData';
import { H1 } from 'foundations-components/transitional/components/H1';
import { View } from 'foundations-components/components/View';
import { isSoftwareSignup } from '../store/flow/selectors';
import { useInSidePanelLayoutExperiment } from '../hooks/useInSidePanellayoutExperiment';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledLoadingContainer = styled.div.withConfig({
  displayName: "BaseLoadingView__StyledLoadingContainer"
})(["", ""], props => (props.$layoutType === LayoutType.FramedCentredStep || props.$layoutType === LayoutType.FramedCentredModalStep) && `  margin: auto;
    vertical-align: middle;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);`);
const StyledTextContainer = styled.div.withConfig({
  displayName: "BaseLoadingView__StyledTextContainer"
})(["min-height:30px;"]);
const BaseLoadingView = ({
  loadingKey,
  hasSpinner = true,
  imageUrl,
  textDisplayDelay = 0,
  isIllustration,
  hasConfetti = false,
  illustrationHeight,
  hasLoadingDots,
  setTimeoutForCopyCarousel = 0
}) => {
  const {
    titleI18n,
    subtitleI18n
  } = useLoadingTitle({
    loadingKey
  });
  const canSwitchCopy = useSelector(getCanSwitchCopy(loadingKey));
  const {
    copyToDisplay: titleToDisplay
  } = useCopyCarousel({
    baseI18n: titleI18n,
    loadingKey,
    canSwitchCopy,
    textDisplayDelay,
    setTimeoutForCopyCarousel
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showSpinner, setShouldShowSpinner] = useState(true);
  const [shouldShowText, setShouldShowText] = useState(textDisplayDelay === 0);
  const shouldNotAnimate = Boolean(useSelector(getActiveLoading));
  useSetErrorAfterTimeout(loadingKey);
  const {
    layoutType
  } = useLayoutTemplate();
  const {
    isMobile
  } = useResponsiveness();
  const {
    data: firstName
  } = useSignupData(Data.FirstName);
  const {
    isInSidePanelLayoutExperiment
  } = useInSidePanelLayoutExperiment();
  const renderLoadingIcon = () => {
    const shouldRenderSpinner = hasSpinner || !imageLoaded && showSpinner;
    if (hasLoadingDots || shouldRenderSpinner && isInSidePanelLayoutExperiment) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        minHeight: 100,
        size: "md"
      });
    }
    return shouldRenderSpinner && /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      minHeight: 100,
      size: "md"
    });
  };
  const renderImage = () => {
    if (isIllustration && imageUrl) {
      return /*#__PURE__*/_jsx(Illustration, {
        alt: "Loading illustration",
        height: illustrationHeight,
        marginBlockEnd: "600",
        marginBlockStart: "400",
        marginInline: "auto",
        name: imageUrl,
        onLoad: () => {
          setImageLoaded(true);
          setShouldShowSpinner(false);
        }
      });
    }
    return imageUrl && /*#__PURE__*/_jsx("div", {
      style: {
        display: imageLoaded ? 'block' : 'none'
      },
      children: /*#__PURE__*/_jsx(Image, {
        margin: "auto",
        marginBlockEnd: "400",
        marginBlockStart: "200",
        maxBlockSize: isMobile ? '200px' : '300px',
        src: imageUrl,
        onLoad: () => {
          setImageLoaded(true);
          setShouldShowSpinner(false);
        }
      })
    });
  };
  const renderPretitleText = () => {
    const pretitleTextKey = `loading.${loadingKey}.preTitle`;
    if (isValidI18nKey(pretitleTextKey) && isSoftwareSignup()) {
      return /*#__PURE__*/_jsx(View, {
        as: "div",
        minBlockSize: "40px",
        children: /*#__PURE__*/_jsx(AnimatedStepBody, {
          children: /*#__PURE__*/_jsx(H1, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: pretitleTextKey,
              options: {
                name: firstName
              }
            })
          })
        }, pretitleTextKey)
      });
    }
    return null;
  };
  const renderText = () => {
    if ((imageUrl && imageLoaded || showSpinner) && shouldShowText) {
      const helperTextKey = `loading.${loadingKey}.helperText`;
      if (isValidI18nKey(helperTextKey) && isSoftwareSignup() && !isInSidePanelLayoutExperiment) {
        return /*#__PURE__*/_jsx(StyledTextContainer, {
          children: /*#__PURE__*/_jsx(LoadingTitle, {
            loadingKey: loadingKey,
            title: helperTextKey
          })
        });
      }
      return /*#__PURE__*/_jsxs(StyledTextContainer, {
        children: [isValidI18nKey(titleToDisplay) && /*#__PURE__*/_jsx(LoadingTitle, {
          loadingKey: loadingKey,
          title: titleToDisplay
        }), isValidI18nKey(subtitleI18n) && /*#__PURE__*/_jsx(FormattedMessage, {
          message: subtitleI18n
        })]
      });
    }
    return null;
  };
  useEffect(() => {
    const timeout = setTimeout(() => setShouldShowText(true), textDisplayDelay);
    return () => {
      clearTimeout(timeout);
    };
  }, [shouldShowText, textDisplayDelay]);
  return /*#__PURE__*/_jsxs(AnimatedStepBody, {
    disableAnimation: shouldNotAnimate,
    children: [hasConfetti && /*#__PURE__*/_jsx(Confetti, {}), /*#__PURE__*/_jsx(NavMarker, {
      name: "LOADING"
    }), /*#__PURE__*/_jsx(StyledLoadingContainer, {
      $layoutType: layoutType,
      "data-selenium": loadingKey,
      children: /*#__PURE__*/_jsxs(LoadingCentered, {
        "data-test-id": "loading-container",
        maxHeight: 450,
        children: [!isInSidePanelLayoutExperiment && renderPretitleText(), renderLoadingIcon(), renderImage(), renderText(), !isInSidePanelLayoutExperiment && imageLoaded && isSoftwareSignup() && /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true,
          minHeight: isMobile ? 50 : 100,
          size: "sm"
        }), isInSidePanelLayoutExperiment && imageLoaded && /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true,
          minHeight: 100
        })]
      })
    })]
  });
};
export default BaseLoadingView;